import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { SeoMetaPort } from '../../infrastructure/seo-meta.port';
import { ISeoResource } from '../../infrastructure/resources/seo/seo-resource.interface';
import { FONT_PRELOADS } from '../../infrastructure/resources/seo/font-preloads';
import { OgPayload } from '../../infrastructure/resources/seo/og-payload';
import { PageSeoPayload } from '../../infrastructure/resources/seo/page-seo-payload';
import { SeoLocale } from '../../domain/entities/seo-locale';
import {
    deriveStrippedPathFromCurrentPath,
    isNonPageRequestPath,
} from '../../domain/helpers/current-path.helper';
import { PublicSeoHreflangLink, RouteContract } from '../../infrastructure';
import { RoutesRepository } from '../repositories/routes.repository';
import { BUY_SELL_SEO_SYSTEM_NAME } from '../../domain/entities/buy-sell-seo-route-data';

@Injectable()
export class ApplyRouteSeoUseCase {
    public constructor(
        private readonly seoMetaPort: SeoMetaPort,
        private readonly routesRepository: RoutesRepository,
    ) {}

    public execute(
        currentPath: string,
        currentLocale: SeoLocale,
        routeSnapshot?: ActivatedRouteSnapshot,
    ): void {
        if (isNonPageRequestPath(currentPath)) {
            return;
        }

        const strippedPath: string = deriveStrippedPathFromCurrentPath(currentPath);
        const systemName: string = this.resolveSystemName(routeSnapshot);
        const route: RouteContract | undefined = this.routesRepository.findMatchingRoute(
            strippedPath,
            systemName,
        );

        if (route === undefined) {
            return;
        }

        const seoResource: ISeoResource = route.getSeoResource();
        const pageSeo: PageSeoPayload = seoResource.buildPageSeo(currentPath, currentLocale);
        const canonical: string = seoResource.buildCanonical(currentPath, currentLocale);
        const hreflangs: ReadonlyArray<PublicSeoHreflangLink> =
            seoResource.buildHreflangs(currentPath);
        const robots: string = seoResource.buildRobots(currentPath);
        const htmlLang: string = seoResource.buildHtmlLang(currentPath, currentLocale);
        const ogPayload: OgPayload = seoResource.buildOpenGraph(currentPath, currentLocale);

        this.seoMetaPort.setPageSeo(pageSeo);
        this.seoMetaPort.setCanonical(canonical);
        this.seoMetaPort.setHreflangs(hreflangs);
        this.seoMetaPort.setRobots(robots);
        this.seoMetaPort.setHtmlLang(htmlLang);
        this.seoMetaPort.setOpenGraph(ogPayload);
        this.seoMetaPort.setPreloads(FONT_PRELOADS);
        this.seoMetaPort.setJsonLd(seoResource.buildJsonLd?.(currentPath, currentLocale) ?? null);
    }

    private resolveSystemName(snapshot?: ActivatedRouteSnapshot): string {
        let current: ActivatedRouteSnapshot | null = snapshot ?? null;
        while (current !== null) {
            const name: unknown = current.data[BUY_SELL_SEO_SYSTEM_NAME];
            if (typeof name === 'string') {
                return name;
            }
            current = current.firstChild;
        }

        return '';
    }
}
